import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { roleLabel } from "../lib/api";
import {
  LayoutDashboard, Folders, ListChecks, CalendarClock, ShieldCheck,
  PackageSearch, Wrench, Wallet, BarChart3, Users, Sun, LogOut, Search,
  Command, Settings as SettingsIcon, UserPlus, Menu, X, KeyRound, MapPin, Pencil,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sheet, SheetContent } from "./ui/sheet";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChangePasswordDialog } from "./ChangePasswordDialog";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/daily", label: "Daily Review", icon: Sun, testid: "nav-daily" },
  { to: "/projects", label: "Projects", icon: Folders, testid: "nav-projects" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks" },
  { to: "/design", label: "Design & Drawings", icon: Pencil, testid: "nav-design" },
  { to: "/approvals", label: "Approvals", icon: ShieldCheck, testid: "nav-approvals" },
  { to: "/procurement", label: "Procurement", icon: PackageSearch, testid: "nav-procurement" },
  { to: "/calendar", label: "Install Calendar", icon: CalendarClock, testid: "nav-calendar" },
  { to: "/site-visits", label: "Site Visits", icon: MapPin, testid: "nav-site-visits" },
  { to: "/tickets", label: "Service Tickets", icon: Wrench, testid: "nav-tickets" },
  { to: "/finance", label: "Payments", icon: Wallet, testid: "nav-finance" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/team", label: "Team", icon: Users, testid: "nav-team" },
  { to: "/hr", label: "Human Resources", icon: UserPlus, testid: "nav-hr" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

function SidebarBody({ user, logout, onNavigate, onChangePassword }) {
  return (
    <div className="h-full flex flex-col">
      <div className="p-5 border-b border-border bg-[hsl(var(--brand-blue))/0.08]">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-[hsl(var(--brand-blue))] grid place-items-center shadow-lg shadow-[hsl(var(--brand-blue))/40]">
            <Command className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="tappav-wordmark text-base leading-none">
              Tapp<span className="tappav-av">AV</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">Ops Center</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={n.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[hsl(var(--brand-blue))/0.18] text-white border border-[hsl(var(--brand-blue))/0.5] shadow-[inset_2px_0_0_0_hsl(var(--brand-yellow))]"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/50 border border-transparent"
              }`
            }
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-3 p-2 rounded-md bg-secondary/40">
          <div className="h-8 w-8 rounded-full bg-[hsl(var(--brand-yellow))] grid place-items-center text-xs font-bold text-[hsl(218_65%_10%)]">
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {roleLabel(user?.role)}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onChangePassword?.()}
            data-testid="change-password-btn"
            className="h-8 w-8"
            title="Change password"
          >
            <KeyRound className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={async () => { await logout(); toast.success("Signed out"); }}
            data-testid="logout-btn"
            className="h-8 w-8"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [search, setSearch] = useState("");
  const [now, setNow] = useState(() => new Date());
  const [mobileOpen, setMobileOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Auto-open change-password if forced
  const mustChange = !!user?.must_change_password;
  useEffect(() => {
    if (mustChange) setChangePwOpen(true);
  }, [mustChange]);

  const doSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    window.location.href = `/projects?q=${encodeURIComponent(search)}`;
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar — desktop only */}
      <aside className="hidden lg:flex w-60 shrink-0 border-r border-border bg-card/40 flex-col" data-testid="sidebar">
        <SidebarBody user={user} logout={logout} onChangePassword={() => setChangePwOpen(true)} />
      </aside>

      {/* Sidebar — mobile Sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0 bg-card border-border" data-testid="mobile-sidebar">
          <SidebarBody user={user} logout={logout} onNavigate={() => setMobileOpen(false)} onChangePassword={() => setChangePwOpen(true)} />
        </SheetContent>
      </Sheet>

      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} forced={mustChange} />

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card/40 backdrop-blur-md sticky top-0 z-10 flex items-center gap-2 sm:gap-4 px-3 sm:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden shrink-0"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>

          {/* Mobile brand mark (visible only on <lg) */}
          <div className="lg:hidden tappav-wordmark text-sm leading-none">
            Tapp<span className="tappav-av">AV</span>
          </div>

          <form onSubmit={doSearch} className="flex-1 max-w-md relative hidden sm:block">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search projects, clients, tasks, owners…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-secondary/60 border-border h-9"
              data-testid="global-search-input"
            />
          </form>

          <div className="ml-auto flex items-center gap-2 text-xs sm:text-sm font-mono text-muted-foreground" data-testid="header-datetime">
            <span className="text-[hsl(var(--brand-yellow))] font-semibold">
              <span className="hidden md:inline">
                {now.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
              </span>
              <span className="md:hidden">
                {now.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
              </span>
            </span>
            <span className="text-border hidden md:inline">·</span>
            <span className="tabular-nums">
              {now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        </header>
        <main className="flex-1 p-3 sm:p-4 overflow-y-auto overflow-x-hidden" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
